/**
 * `<game-table>`: the playing surface - seats on the rail, each team's melds
 * and books on the felt in front of them, the stock and discard pile in the
 * middle - with the turn bar underneath and the history alongside.
 *
 * This is the one component with state of its own: which cards the player has
 * picked out, and which melds they have built toward an opener but not yet
 * played. Everything else is redrawn from the server's latest view, which is
 * authoritative, so the selection is always re-checked against what is held.
 */

import type { GameView, MeldableRank, Team } from "@ryzun/haf-saloon-game-engine";
import type { TeamPresentation } from "../../server/protocol.js";
import { h, keepFocus } from "../dom.js";
import { type ActionDetail, type InspectDetail, emit } from "../events.js";
import {
    type Airborne,
    type Flight,
    type Point,
    type StockDraw,
    DRAW_STAGGER_MS,
    NOTHING_AIRBORNE,
    addDraw,
    findStockDraw,
    getCenter,
    getDealOrigin,
    getDealtAirborne,
    getSeatTurn,
    getTurnBetween,
    holdBack,
    isGrounded,
    land,
    planDeal
} from "../flights.js";
import { sortHand, toggleSelection } from "../hand.js";
import { type SeatChange, type SeatHolder, describeSeatChange } from "../lobby.js";
import type { LoggedEvent } from "../log.js";
import { TEAM_COLOR_LABELS } from "../palette.js";
import {
    type LayDownSurface,
    type StagedMeld,
    type TurnOptions,
    describeAddFailure,
    getPlayableCardIds,
    getStagedCardIds,
    getTurnOptions,
    stageSelection,
    toStagedTiles
} from "../play.js";
import { findPartner, placeSeats } from "../seating.js";
import {
    type SeatRole,
    describePhase,
    describeTurnNote,
    formatElapsed,
    getTeamColor,
    getTeamName,
    getTurnAlert
} from "../table-text.js";
import {
    type TallyProgress,
    type TallyStep,
    NOTHING_TALLIED,
    countStep,
    describeHeldThree,
    describeStage,
    describeTallyStart,
    formatPoints,
    getBookTallies,
    getTalliedView,
    isHeldThree,
    liftCard,
    planTally
} from "../tally.js";
import { BaseElement } from "./base-element.js";
import type { FlightPath } from "./card-flights.js";
import styles from "./game-table.css";
import "./card-flights.js";
import "./center-well.js";
import "./meld-tray.js";
import "./score-board.js";
import "./table-seat.js";
import "./tableau-area.js";
import "./team-history.js";
import "./turn-bar.js";
import "./turn-log.js";

/** How long the reason a meld refused the selection stays up. */
const REFUSAL_MS = 3000;

/** A server rejection is rarer and less expected, so it stays up a little longer. */
const NOTICE_MS = 5000;

/** How long a note about someone joining or leaving stays by their seat; matches its fade. */
const CALLOUT_MS = 6000;

/** How long the table id says its link was copied. */
const COPIED_MS = 2000;

/** How often the game clock is redrawn; it only shows whole seconds. */
const CLOCK_TICK_MS = 1000;

/** The table this game is at, and the link that brings someone else to it. */
export interface TableInvite {
    readonly tableId: string;
    readonly url: string;
}

interface Toast {
    readonly id: number;
    readonly text: string;
}

/** A message over the felt, which a correction of the mistake it reports takes down. */
interface Notice extends Toast {
    /** True when the next play the server accepts makes the message moot. */
    readonly dismissOnPlay: boolean;
}

/** A team that just lost points to a three, and when, so its score can flash. */
interface Penalty {
    readonly teamId: string;
    /** On the `performance.now()` clock. */
    readonly start: number;
}

/** A finished round being counted out onto the discard pile. */
interface Counting {
    readonly round: number;
    readonly progress: TallyProgress;
    /** True once the last card has landed and the summary has taken over. */
    readonly done: boolean;
    readonly penalty: Penalty | null;
}

/** Where a counted card leaves from, and how it is turned there. */
interface TallySource {
    readonly box: DOMRect;
    readonly turn: number;
}

/** What the current view and selection add up to, recomputed on demand. */
interface Derived {
    readonly view: GameView;
    readonly options: TurnOptions;
    /** The selection, less anything no longer held, in the order it was picked. */
    readonly selectedIds: readonly string[];
    /** The hand minus whatever is set aside in the lay-down. */
    readonly heldHand: TurnOptions["activeHand"];
}

export class GameTable extends BaseElement {
    #view: GameView | null = null;

    #teamColors: readonly TeamPresentation[] = [];

    #events: readonly LoggedEvent[] = [];

    #announcement = "";

    /** Card ids the player has picked out, in the order they picked them. */
    #selected: readonly string[] = [];

    /**
     * Melds built but not yet played. An opener is often two or three melds that
     * only clear the round minimum together (§5), so they are gathered here and
     * sent as a single play.
     */
    #staged: readonly StagedMeld[] = [];

    /**
     * True from sending an action until the server answers it. The selection and
     * lay-down are held as they were until then: clearing them straight away put
     * the cards back in the hand for a frame before the new view took them away.
     */
    #awaitingReply = false;

    #toasts: readonly Notice[] = [];

    /** The messages last put on the felt; rebuilding them each render restarted their entrance. */
    #shownToasts: readonly Notice[] = [];

    #toastId = 0;

    /** Who is playing each seat, by player id; a seat missing from it is a person's. */
    #holders: Readonly<Record<string, SeatHolder>> = {};

    /** Notes standing beside seats about who just came or went, by player id. */
    #callouts: Readonly<Record<string, Toast>> = {};

    #tray: InspectDetail | null = null;

    /** The team whose round-by-round history is open, if any. */
    #historyTeamId: string | null = null;

    /** The players the seats were built for; a new line-up rebuilds them. */
    #seatKey = "";

    /** Cards dealt or drawn but still on their way, which the seats hold back until they land. */
    #airborne: Airborne = NOTHING_AIRBORNE;

    /** The window the server gave the next view's deal, or null when it is not a deal. */
    #dealWindowMs: number | null = null;

    /** True while a deal is going round; the felt takes no input until it is done. */
    #dealing = false;

    /** The window the server gave the next view's count, or null when it does not end a round. */
    #tallyWindowMs: number | null = null;

    /**
     * The round being counted out. It stays once the count is done, so the
     * table behind the summary stays as the count left it, until the next deal.
     */
    #counting: Counting | null = null;

    #invite: TableInvite | null = null;

    /** When the game began, by this page's clock, or null when there is no game. */
    #startedAt: number | null = null;

    /** When the game was seen to finish, which stops the clock; null while it runs. */
    #endedAt: number | null = null;

    #clockTimer: ReturnType<typeof setInterval> | null = null;

    #copiedTimer: ReturnType<typeof setTimeout> | null = null;

    readonly #felt = h("div", { class: "felt" });

    readonly #flights = document.createElement("card-flights");

    readonly #toastLayer = h("div", { class: "toasts" });

    /** What the count is on, over the well; it reads out each stage and every three. */
    readonly #tallyNote = h("p", { class: "tally-note", role: "status", hidden: true });

    readonly #well = document.createElement("center-well");

    readonly #bar = document.createElement("turn-bar");

    readonly #standings = h("table", { "class": "standings", "aria-label": "Scores" });

    readonly #log = document.createElement("turn-log");

    readonly #tableLink = h("button", { type: "button", class: "table-link" });

    readonly #clock = h("span", {});

    /** A timer role is not live, so the clock does not read itself out every second. */
    readonly #timer: HTMLSpanElement = h("span", { class: "timer", role: "timer" }, h("span", { class: "sr-only" }, "Game time "), this.#clock);

    readonly #tableInfo = h("div", { class: "table-info" }, this.#tableLink, this.#timer);

    readonly #side = h("aside", { class: "side" }, this.#tableInfo, this.#standings, this.#log);

    /** Reads out comings and goings; the notes by the seats are hidden from screen readers. */
    readonly #seatNews = h("p", { "class": "sr-only", "role": "status", "aria-live": "polite" });

    readonly #trayView = document.createElement("meld-tray");

    readonly #trayDialog = h("dialog", { class: "tray" });

    readonly #historyView = document.createElement("team-history");

    readonly #historyDialog = h("dialog", { class: "history" });

    readonly #interlude = h("div", { class: "interlude", role: "status", hidden: true });

    readonly #interludeHeading = h("h2", { tabindex: -1 });

    readonly #interludeNote = h("p", { class: "muted" });

    readonly #scores = document.createElement("score-board");

    readonly #backButton = h("button", { type: "button", class: "primary" }, "Back to the saloon");

    readonly #onWindowKeyDown = (event: KeyboardEvent): void => this.#onKeyDown(event);

    constructor() {
        super(styles);

        const close = h("button", { type: "button", class: "close" }, "Close");

        close.addEventListener("click", () => this.#closeTray());
        this.#trayDialog.addEventListener("close", () => {
            this.#tray = null;
        });
        this.#trayDialog.append(this.#trayView, close);

        const closeHistory = h("button", { type: "button", class: "close" }, "Close");

        closeHistory.addEventListener("click", () => this.#closeHistory());
        this.#historyDialog.addEventListener("close", () => {
            this.#historyTeamId = null;
        });
        this.#historyDialog.append(this.#historyView, closeHistory);

        this.#tableLink.addEventListener("click", () => void this.#copyInvite());
        this.#backButton.addEventListener("click", () => emit(this, "leave-table", null));
        this.#interlude.append(h(
            "div",
            { class: "panel" },
            this.#interludeHeading,
            this.#scores,
            this.#interludeNote,
            this.#backButton
        ));

        this.#felt.append(this.#well, this.#flights, this.#toastLayer, this.#tallyNote);

        this.root.append(
            h("div", { class: "table-surface" }, this.#felt),
            this.#bar,
            this.#side,
            this.#seatNews,
            this.#trayDialog,
            this.#historyDialog,
            this.#interlude
        );

        this.#listen();
    }

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener("keydown", this.#onWindowKeyDown);
        this.#clockTimer = setInterval(() => this.#renderClock(), CLOCK_TICK_MS);
    }

    disconnectedCallback(): void {
        window.removeEventListener("keydown", this.#onWindowKeyDown);

        if (this.#clockTimer !== null) {
            clearInterval(this.#clockTimer);
            this.#clockTimer = null;
        }
    }

    /** The server's latest view of the table, or null between games. */
    get view(): GameView | null {
        return this.#view;
    }

    set view(value: GameView | null) {
        const previous = this.#view;

        // The app hands over the same view on every update, so only a new one
        // is the server's answer to an action in flight. An accepted play also
        // settles whatever mistake a message was still pointing out, so the
        // message goes with it.
        if (value !== previous && this.#awaitingReply) {
            this.#awaitingReply = false;
            this.#selected = [];
            this.#staged = [];
            this.#dropToasts((toast) => toast.dismissOnPlay);
        }

        this.#view = value;

        if (value !== previous) {
            this.#animate(previous, value);
        }

        this.invalidate();
    }

    /**
     * Marks the next new view as a fresh deal, which the server holds the table
     * still for `windowMs` to let it play out.
     */
    expectDeal(windowMs: number): void {
        this.#dealWindowMs = windowMs;
    }

    /**
     * Marks the next new view as the end of a round, which the server gives
     * `windowMs` to be counted out before it deals again.
     */
    expectTally(windowMs: number): void {
        this.#tallyWindowMs = windowMs;
    }

    /** The table being played at, whose link the id over the scores copies. */
    set invite(value: TableInvite | null) {
        this.#invite = value;
        this.invalidate();
    }

    /** When the game began, by this page's clock; the game clock counts up from it. */
    set startedAt(value: number | null) {
        this.#startedAt = value;
        this.invalidate();
    }

    set teamColors(value: readonly TeamPresentation[]) {
        this.#teamColors = value;
        this.invalidate();
    }

    set events(value: readonly LoggedEvent[]) {
        this.#events = value;
        this.invalidate();
    }

    /** The latest action in full, announced to screen readers even when the feed skips it. */
    set announcement(value: string) {
        this.#announcement = value;
        this.invalidate();
    }

    /** Who is playing each seat: a person, a bot, or a bot covering for someone away. */
    set holders(value: Readonly<Record<string, SeatHolder>>) {
        this.#holders = value;
        this.invalidate();
    }

    /**
     * Points out people joining or leaving mid-game. Each gets a note beside
     * the seat it happened at, rather than a line in the activity feed, which
     * is kept for the play itself.
     */
    noteSeatChanges(changes: readonly SeatChange[]): void {
        if (changes.length === 0) {
            return;
        }

        const texts = changes.map((change) => ({ change, text: describeSeatChange(change) }));

        for (const { change, text } of texts) {
            const id = ++this.#toastId;

            this.#callouts = { ...this.#callouts, [change.playerId]: { id, text: text.callout } };

            setTimeout(() => {
                if (this.#callouts[change.playerId]?.id === id) {
                    this.#callouts = Object.fromEntries(Object.entries(this.#callouts)
                        .filter(([playerId]) => playerId !== change.playerId));
                    this.invalidate();
                }
            }, CALLOUT_MS);
        }

        this.#seatNews.textContent = texts.map(({ text }) => text.announcement).join(" ");
        this.invalidate();
    }

    /**
     * Puts a short message over the felt - used for an action the server
     * refused, which the activity feed no longer carries.
     */
    notify(text: string): void {
        this.#pushToast(text, NOTICE_MS, false);
    }

    /**
     * Reports an action the server refused. Nothing changed on the table, so
     * the selection and lay-down are left as they were for the player to fix.
     */
    reject(text: string): void {
        this.#awaitingReply = false;
        this.#pushToast(text, NOTICE_MS, true);
    }

    /** Clears the player's own half-built state, for a fresh table. */
    reset(): void {
        this.#awaitingReply = false;
        this.#selected = [];
        this.#staged = [];
        this.#toasts = [];
        this.#callouts = {};
        this.#seatNews.textContent = "";
        this.#dealWindowMs = null;
        this.#tallyWindowMs = null;
        this.#endedAt = null;
        this.#ground();
        this.#closeTray();
        this.#closeHistory();
        this.invalidate();
    }

    /**
     * Plays out whatever moved cards between two views: the whole deal for a
     * fresh round, the two cards off the stock for a draw, or the count of a
     * round that just ended. Anything else just appears, as it always has.
     */
    #animate(previous: GameView | null, next: GameView | null): void {
        const windowMs = this.#dealWindowMs;
        const tallyWindowMs = this.#tallyWindowMs;

        this.#dealWindowMs = null;
        this.#tallyWindowMs = null;

        if (next === null || matchMedia("(prefers-reduced-motion: reduce)").matches) {
            this.#ground();
            return;
        }

        if (next.status !== "in-round") {
            if (tallyWindowMs !== null) {
                this.#countRound(next, tallyWindowMs);
            }
            // The same scored round sent again, for someone joining, leaves the count running.
            else if (this.#counting?.round !== next.round) {
                this.#ground();
            }

            return;
        }

        if (windowMs !== null) {
            this.#deal(next, windowMs);
            return;
        }

        const draw = previous === null ? null : findStockDraw(previous, next);

        if (previous !== null && draw !== null) {
            this.#draw(previous, next, draw);
        }
    }

    /** Calls off every card in the air and shows the table as it stands. */
    #ground(): void {
        this.#flights.cancel();
        this.#airborne = NOTHING_AIRBORNE;
        this.#dealing = false;
        this.#counting = null;
    }

    /**
     * Counts a finished round out onto the discard pile: book bonuses first,
     * then every card in the books, then the melds, then whatever is still
     * held, each going round the teams from the dealer's. The scores climb as
     * the cards land, and the summary waits until the last one has.
     */
    #countRound(view: GameView, windowMs: number): void {
        this.#ground();
        this.#counting = { round: view.round, progress: NOTHING_TALLIED, done: false, penalty: null };
        this.#setTallyNote(describeTallyStart(view), false);

        const plan = planTally(view, windowMs);

        for (const [index, step] of plan.steps.entries()) {
            const previous = plan.steps[index - 1];
            const opens = previous?.teamId !== step.teamId || previous.stage !== step.stage;

            // A bonus moves no card, so it only waits its turn and lands at once.
            const measure = (): FlightPath | null => {
                const path = step.card === null ? null : this.#tallyPath(step);

                this.#liftStep(view, step, opens);

                return path;
            };

            void this.#flights.fly(step.delayMs, measure).then((landed) => {
                if (landed) {
                    this.#settleStep(step);
                }
            });
        }

        void this.#flights.fly(plan.durationMs, () => null).then((landed) => {
            if (landed && this.#counting !== null) {
                this.#counting = { ...this.#counting, done: true };
                this.invalidate();
            }
        });
    }

    /** A step begins: its card leaves the table, and the note says what is being counted. */
    #liftStep(view: GameView, step: TallyStep, opens: boolean): void {
        const counting = this.#counting;

        if (counting === null) {
            return;
        }

        const holder = view.players.find((player) => player.id === step.heldBy?.playerId);

        if (step.card !== null && holder !== undefined && isHeldThree(step)) {
            this.#setTallyNote(describeHeldThree(holder.name, step.card), true);
        }
        else if (opens) {
            this.#setTallyNote(`${this.#teamLabel(step.teamId)} · ${describeStage(step.stage)}`, false);
        }

        this.#counting = { ...counting, progress: liftCard(counting.progress, step) };
        this.invalidate();
    }

    /** A step lands: its points go on the board, and a three sets its team's score flashing. */
    #settleStep(step: TallyStep): void {
        const counting = this.#counting;

        if (counting === null) {
            return;
        }

        const now = performance.now();

        this.#counting = {
            ...counting,
            progress: countStep(counting.progress, step, now),
            penalty: isHeldThree(step) ? { teamId: step.teamId, start: now } : counting.penalty
        };
        this.invalidate();
    }

    #setTallyNote(text: string, penalty: boolean): void {
        this.#tallyNote.textContent = text;
        this.#tallyNote.classList.remove("penalty");

        // Reading layout restarts the animation when two threes come in a row.
        void this.#tallyNote.offsetWidth;
        this.#tallyNote.classList.toggle("penalty", penalty);
    }

    #teamLabel(teamId: string): string {
        return `${TEAM_COLOR_LABELS[getTeamColor(this.#teamColors, teamId)]} Team`;
    }

    /** The way from where a counted card lies to the discard pile, or null when it cannot be seen. */
    #tallyPath(step: TallyStep): FlightPath | null {
        const layer = this.#flights.getBoundingClientRect();
        const source = this.#tallySource(step);

        if (source === null || step.card === null || layer.width === 0 || source.box.width === 0) {
            return null;
        }

        return {
            from: getCenter(source.box, layer),
            to: getCenter(this.#well.discardBox, layer),
            fromTurn: source.turn,
            toTurn: source.turn + getTurnBetween(source.turn, 0),
            scale: 1,
            card: step.card,
            durationMs: step.flightMs,
            emphasis: isHeldThree(step)
        };
    }

    /** A card leaves its book or meld on the felt, or its owner's hand or face-down Foot. */
    #tallySource(step: TallyStep): TallySource | null {
        const heldBy = step.heldBy;

        if (heldBy !== null) {
            const seat = this.#seatFor(heldBy.playerId);

            if (seat === null) {
                return null;
            }

            // Once picked up, a Foot is the hand the player holds.
            return heldBy.pile === "foot" && seat.seat?.player.inFoot !== true
                ? { box: seat.footBox, turn: 0 }
                : { box: seat.handBox, turn: this.#turnFor(heldBy.playerId) };
        }

        const kind = step.stage === "books" ? "books" : "melds";
        const area = this.#felt.querySelector<HTMLElementTagNameMap["tableau-area"]>(
            `tableau-area[data-team-id="${CSS.escape(step.teamId)}"][data-kind="${kind}"]`
        );
        const box = step.rank === null ? null : area?.pileBox(step.rank) ?? null;

        if (area === null || box === null) {
            return null;
        }

        // An area is turned to face its seat; the computed turn is "none" for one left upright.
        return { box, turn: Number.parseFloat(getComputedStyle(area).rotate) || 0 };
    }

    #deal(view: GameView, windowMs: number): void {
        this.#ground();
        this.#airborne = getDealtAirborne(view);
        this.#dealing = true;

        const dealerId = view.players[view.dealerIndex]?.id ?? "";

        // Measured as each card leaves: on a first deal the seats are only
        // built by the render this view queues.
        for (const flight of planDeal(view, windowMs)) {
            const measure = (): FlightPath | null => {
                const seat = this.#seatFor(dealerId);

                if (seat === null) {
                    return null;
                }

                const layer = this.#flights.getBoundingClientRect();
                const middle = { x: layer.width / 2, y: layer.height / 2 };
                const origin = getDealOrigin(getCenter(seat.handBox, layer), middle);

                return this.#pathTo(flight, origin, this.#turnFor(dealerId));
            };

            void this.#flights.fly(flight.delayMs, measure).then((landed) => this.#land(landed, flight, view.viewerId));
        }
    }

    #draw(previous: GameView, next: GameView, draw: StockDraw): void {
        const flight: Flight = { playerId: draw.playerId, target: "hand", delayMs: 0 };
        const measure = (): FlightPath | null => {
            const layer = this.#flights.getBoundingClientRect();

            return this.#pathTo(flight, getCenter(this.#well.stockBox, layer), 0);
        };

        this.#airborne = addDraw(this.#airborne, previous, next, draw);

        for (let index = 0; index < draw.count; index += 1) {
            void this.#flights.fly(index * DRAW_STAGGER_MS, measure)
                .then((landed) => this.#land(landed, flight, next.viewerId));
        }
    }

    #land(landed: boolean, flight: Flight, viewerId: string): void {
        if (!landed) {
            return;
        }

        this.#airborne = land(this.#airborne, flight, viewerId);
        this.#dealing = this.#dealing && !isGrounded(this.#airborne);
        this.invalidate();
    }

    /**
     * The way from a point on the felt to the seat a card is meant for, or null
     * when there is nowhere visible to fly it - the card then lands at once.
     */
    #pathTo(flight: Flight, from: Point, fromTurn: number): FlightPath | null {
        const seat = this.#seatFor(flight.playerId);
        const layer = this.#flights.getBoundingClientRect();

        if (seat === null || layer.width === 0) {
            return null;
        }

        const isFoot = flight.target === "foot";
        const box = isFoot ? seat.footBox : seat.handBox;

        if (box.width === 0) {
            return null;
        }

        // A Foot sits upright beside its owner's avatar, at token size.
        const toTurn = isFoot ? 0 : this.#turnFor(flight.playerId);
        const footScale = Number.parseFloat(getComputedStyle(this.#felt).getPropertyValue("--foot-scale")) || 1;

        return {
            from,
            to: getCenter(box, layer),
            fromTurn,
            toTurn: fromTurn + getTurnBetween(fromTurn, toTurn),
            scale: isFoot ? footScale : 1
        };
    }

    #turnFor(playerId: string): number {
        const orientation = this.#seatFor(playerId)?.seat?.orientation;

        return orientation === undefined ? 0 : getSeatTurn(orientation);
    }

    #seatFor(playerId: string): HTMLElementTagNameMap["table-seat"] | null {
        return this.#felt.querySelector(`table-seat[data-player-id="${CSS.escape(playerId)}"]`);
    }

    /** The view as the table shows it: during a count, less every card already counted. */
    #shownView(): GameView | null {
        const view = this.#view;
        const counting = this.#counting;

        return view === null || counting === null ? view : getTalliedView(view, counting.progress);
    }

    #derive(): Derived | null {
        const view = this.#shownView();

        if (view === null) {
            return null;
        }

        const options = getTurnOptions(view, this.#selected, this.#staged);
        const stagedIds = getStagedCardIds(this.#staged);

        return {
            view,
            options,
            selectedIds: this.#selected.filter((id) => options.activeHand.some((card) => card.id === id)),
            heldHand: options.activeHand.filter((card) => !stagedIds.includes(card.id))
        };
    }

    #listen(): void {
        const root = this.root;

        root.addEventListener("card-toggle", (event) => {
            const derived = this.#derive();

            if (derived !== null) {
                this.#selected = toggleSelection(derived.heldHand, this.#selected, event.detail.cardId);
                this.invalidate();
            }
        });

        root.addEventListener("draw-stock", () => this.#submit({ type: "draw-from-stock" }));

        root.addEventListener("take-discard", () => {
            this.#submit({ type: "draw-from-discard", cardIds: this.#derive()?.selectedIds ?? [] });
        });

        // The pile takes the last card picked, and the rest of the selection is
        // let go with it - a discard ends the turn, so nothing is left held.
        root.addEventListener("discard", () => {
            const card = this.#derive()?.options.discardCard ?? null;

            if (card !== null) {
                this.#submit({ type: "discard", cardId: card.id });
            }
        });

        root.addEventListener("meld-add", (event) => {
            const selectedIds = this.#derive()?.selectedIds ?? [];

            this.#closeTray();
            this.#submit({ type: "play-melds", adds: [{ rank: event.detail.rank, cardIds: selectedIds }] });
        });

        root.addEventListener("meld-create", () => this.#createMeld());

        root.addEventListener("meld-unstage", (event) => {
            this.#staged = this.#staged.filter((entry) => entry.rank !== event.detail.rank);
            this.invalidate();
        });

        root.addEventListener("lay-down", () => {
            this.#submit({ type: "play-melds", creates: this.#staged.map((entry) => entry.cardIds) });
        });

        root.addEventListener("lay-down-clear", () => {
            this.#staged = [];
            this.invalidate();
        });

        root.addEventListener("meld-refuse", (event) => this.#refuse(event.detail.rank));

        root.addEventListener("meld-inspect", (event) => this.#openTray(event.detail));

        root.addEventListener("clear-selection", () => {
            this.#selected = [];
            this.invalidate();
        });

        // Raised by the turn bar inside this shadow tree; the app listens on the host.
        root.addEventListener("leave-table", () => emit(this, "leave-table", null));
    }

    /** The selection and lay-down stay put until the server's reply replaces the view. */
    #submit(action: ActionDetail["action"]): void {
        this.#awaitingReply = true;
        emit(this, "game-action", { action });
    }

    /**
     * Opening a meld is one click once the team is in the game. It is only held
     * back while the team is still working toward the round minimum, because an
     * opener is often two or three melds that clear the bar only together (§5).
     */
    #createMeld(): void {
        const derived = this.#derive();

        if (derived === null) {
            return;
        }

        if (derived.options.team?.inGame === true) {
            this.#submit({ type: "play-melds", creates: [derived.selectedIds] });
            return;
        }

        this.#staged = stageSelection(derived.options.activeHand, derived.selectedIds, this.#staged);
        this.#selected = [];
        this.invalidate();
    }

    /**
     * Escape drops the selection, which is the way out of a half-built meld. An
     * open tray takes it first - closing that is what Escape means there.
     */
    #onKeyDown(event: KeyboardEvent): void {
        const dialogOpen = this.#trayDialog.open || this.#historyDialog.open;

        if (event.key !== "Escape" || this.hidden || dialogOpen || this.#selected.length === 0) {
            return;
        }

        this.#selected = [];
        this.invalidate();
    }

    /** One message at a time: a second replaces the first rather than stacking up. */
    #pushToast(text: string, lifetime: number, dismissOnPlay: boolean): void {
        const id = ++this.#toastId;

        this.#toasts = [{ id, text, dismissOnPlay }];
        this.invalidate();

        setTimeout(() => {
            this.#dropToasts((toast) => toast.id === id);
            this.invalidate();
        }, lifetime);
    }

    /** Takes down the matching messages; when none match, the list is left as it is so nothing is redrawn. */
    #dropToasts(matches: (toast: Notice) => boolean): void {
        if (this.#toasts.some(matches)) {
            this.#toasts = this.#toasts.filter((toast) => !matches(toast));
        }
    }

    /**
     * A refused click on a meld used to be indistinguishable from a missed one.
     * The engine's own reason is put into words over the felt; the pile itself
     * shakes, so the two read together.
     */
    #refuse(rank: MeldableRank): void {
        const derived = this.#derive();

        if (derived === null) {
            return;
        }

        // An empty reason means the engine would in fact take the cards, which
        // the addable check has already ruled out - so it is never the text.
        const reason = describeAddFailure(derived.view, derived.selectedIds, rank)
            || "those cards cannot join this meld";

        this.#pushToast(reason, REFUSAL_MS, true);
    }

    #openTray(target: InspectDetail): void {
        this.#tray = target;
        this.render();

        if (!this.#trayDialog.open) {
            this.#trayDialog.showModal();
        }
    }

    #closeTray(): void {
        this.#tray = null;

        if (this.#trayDialog.open) {
            this.#trayDialog.close();
        }
    }

    #teamFor(view: GameView, teamId: string): Team {
        return view.teams.find((team) => team.id === teamId)
            ?? { id: teamId, playerIds: [], melds: [], inGame: false, score: 0 };
    }

    /**
     * Seats and their meld areas are built once per line-up and point of view,
     * then only updated. The viewer is part of the key because dropping into a
     * different seat turns the whole table round.
     */
    #ensureSeats(view: GameView): void {
        const key = [view.viewerId, ...view.players.map((player) => player.id)].join("|");

        if (key === this.#seatKey) {
            return;
        }

        this.#seatKey = key;
        this.#felt.dataset.seats = String(view.players.length);
        this.#felt.querySelectorAll("table-seat, tableau-area").forEach((element) => element.remove());

        for (const placement of placeSeats(view.players, view.viewerId)) {
            const area = document.createElement("tableau-area");
            const seat = document.createElement("table-seat");

            area.dataset.playerId = placement.player.id;
            area.dataset.teamId = placement.player.teamId;
            area.dataset.kind = placement.tableauRole;
            area.dataset.at = placement.orientation;
            seat.dataset.playerId = placement.player.id;

            this.#felt.append(area, seat);
        }
    }

    /**
     * A table nobody has been dealt at yet - the one behind the seating dialog
     * - is bare felt, not whatever the last game left on it.
     */
    #renderEmpty(): void {
        this.#seatKey = "";
        this.#felt.querySelectorAll("table-seat, tableau-area").forEach((element) => element.remove());
        this.#well.hidden = true;
        this.#bar.hidden = true;
        this.#side.hidden = true;
        this.#interlude.hidden = true;
    }

    protected render(): void {
        const derived = this.#derive();

        if (derived === null) {
            this.#renderEmpty();
            return;
        }

        const { view, options, selectedIds } = derived;

        this.#well.hidden = false;
        this.#bar.hidden = false;
        this.#side.hidden = false;
        this.#ensureSeats(view);
        this.#renderSeats(derived);
        this.#felt.inert = this.#dealing || this.#isCounting();
        this.#tallyNote.hidden = !this.#isCounting();

        this.#well.model = {
            stockCount: view.stockCount,
            discardTop: view.discardTop,
            discardCount: view.discardCount,
            discardFrozen: view.discardFrozen,
            canDrawStock: options.canDrawStock,
            canTakeDiscard: options.canTakeDiscard,
            discardCard: options.discardCard,
            hint: options.discardHint,
            counting: this.#isCounting()
        };

        this.#bar.model = {
            phase: describePhase(options),
            isYourTurn: options.isYourTurn,
            note: describeTurnNote(options, view.roundMinimum, this.#staged.length, selectedIds.length),
            selectedCount: selectedIds.length
        };

        this.#renderTableInfo(view);
        this.#renderStandings(view);
        this.#log.events = this.#events;
        this.#log.roundLive = view.status === "in-round";
        this.#log.announcement = this.#announcement;
        this.#log.alert = getTurnAlert(options);

        this.#renderToasts();

        this.#renderTray(view, options);
        this.#renderHistory(view);
        this.#renderInterlude(this.#view ?? view);
    }

    /** Only a change of message touches the layer, so one already up does not replay its entrance. */
    #renderToasts(): void {
        if (this.#toasts === this.#shownToasts) {
            return;
        }

        this.#shownToasts = this.#toasts;
        this.#toastLayer.replaceChildren(...this.#toasts.map((toast) => h(
            "p",
            { class: "toast", role: "status" },
            toast.text
        )));
    }

    /** True from the end of a round until its last card has been counted. */
    #isCounting(): boolean {
        return this.#counting !== null && !this.#counting.done;
    }

    #renderSeats(derived: Derived): void {
        const { view, options, selectedIds, heldHand } = derived;
        const partner = findPartner(view.players, view.viewerId);

        // The rail under playable runs waits for the cards to settle: drawn
        // while they land, it grew and jumped with every card that dropped in.
        const settling = this.#dealing || this.#airborne.ownIds.length > 0;
        const playable = settling ? new Set<string>() : getPlayableCardIds(view);
        const dealerId = view.players[view.dealerIndex]?.id;

        const layDown: LayDownSurface = {
            canCreate: options.canStageMeld,
            staged: toStagedTiles(options.activeHand, this.#staged),
            stagedValue: options.stagedValue,
            opensRound: options.team !== null && !options.team.inGame,
            roundMinimum: view.roundMinimum,
            canLayDown: options.canLayDown
        };

        // A card still in the air drops into the player's hand when it lands.
        const landed = heldHand.filter((card) => !this.#airborne.ownIds.includes(card.id));

        // Before the first card of a deal lands the hand is face down and
        // empty, rather than face up and reading "No cards left."
        const awaitingDeal = landed.length === 0 && this.#airborne.ownIds.length > 0;

        for (const placement of placeSeats(view.players, view.viewerId)) {
            const { player } = placement;
            const isSelf = player.id === view.viewerId;
            const role: SeatRole = isSelf ? "self" : player.id === partner?.id ? "partner" : "opponent";
            const color = getTeamColor(this.#teamColors, player.teamId);
            const seat = this.#seatFor(player.id);
            const area = this.#felt.querySelector<HTMLElementTagNameMap["tableau-area"]>(`tableau-area[data-player-id="${CSS.escape(player.id)}"]`);

            if (seat !== null) {
                seat.seat = {
                    player: holdBack(player, this.#airborne),
                    orientation: placement.orientation,
                    color,
                    role,
                    clock: placement.clock,
                    isTurn: view.turn?.playerId === player.id,
                    isDealer: dealerId === player.id,
                    holder: this.#holders[player.id] ?? "player",
                    callout: this.#callouts[player.id]?.text ?? ""
                };

                // The hand is always kept in order: the server replaces it on
                // every update, so anything else would shuffle the cards under
                // the player between turns.
                seat.own = isSelf && !awaitingDeal
                    ? {
                        cards: sortHand(landed),
                        selected: selectedIds,
                        playable,
                        selectable: options.selectableIds,
                        label: options.self?.inFoot === true ? "Your Foot" : "Your Hand"
                    }
                    : null;
            }

            if (area !== null) {
                const team = this.#teamFor(view, player.teamId);

                area.model = {
                    team,
                    kind: placement.tableauRole,
                    name: getTeamName(view, player.teamId),
                    color,
                    isOwn: player.teamId === options.team?.id,
                    addableRanks: options.addableRanks,
                    hasSelection: options.isYourTurn && !options.isDrawPhase && selectedIds.length > 0,
                    // The viewer always sits first in the ring, so the melds half of
                    // their own team's tableau is the row in front of them - which
                    // is where the lay-down controls belong.
                    layDown: isSelf && placement.tableauRole === "melds" ? layDown : null,
                    tally: this.#counting === null ? null : getBookTallies(team, this.#counting.progress)
                };
            }
        }
    }

    /**
     * Above the scores: the table's id, which copies the invite link for anyone
     * who wants to bring a friend in mid-game, and how long the game has run.
     */
    #renderTableInfo(view: GameView): void {
        const invite = this.#invite;

        this.#tableLink.hidden = invite === null;

        if (invite !== null && this.#copiedTimer === null) {
            this.#tableLink.textContent = `Table ${invite.tableId}`;
            this.#tableLink.title = "Copy the invite link";
            this.#tableLink.setAttribute("aria-label", `Table ${invite.tableId}, copy the invite link`);
        }

        // The clock stops when the game does, not when the summary is closed.
        if (view.status === "game-complete") {
            this.#endedAt ??= Date.now();
        }
        else {
            this.#endedAt = null;
        }

        this.#renderClock();
    }

    #renderClock(): void {
        const startedAt = this.#startedAt;

        this.#timer.hidden = startedAt === null;

        if (startedAt === null) {
            return;
        }

        const text = formatElapsed((this.#endedAt ?? Date.now()) - startedAt);

        if (this.#clock.textContent !== text) {
            this.#clock.textContent = text;
        }
    }

    async #copyInvite(): Promise<void> {
        const invite = this.#invite;

        if (invite === null) {
            return;
        }

        try {
            await navigator.clipboard.writeText(invite.url);
        }
        catch {
            // No clipboard outside a secure context, so show the link instead
            // for the player to copy themselves.
            this.notify(`Invite link: ${invite.url}`);
            return;
        }

        this.#tableLink.textContent = "Link copied";
        this.#tableLink.setAttribute("aria-label", "Invite link copied");

        if (this.#copiedTimer !== null) {
            clearTimeout(this.#copiedTimer);
        }

        this.#copiedTimer = setTimeout(() => {
            this.#copiedTimer = null;
            this.invalidate();
        }, COPIED_MS);
    }

    /**
     * Each team's banked score from the rounds already scored, named by the
     * color it plays under. While a finished round is counted out, an indented
     * line beneath it keeps the round's running count, and the banked score
     * climbs with it. Each name opens that team's round-by-round history.
     */
    #renderStandings(view: GameView): void {
        const counting = this.#isCounting() ? this.#counting : null;
        const now = performance.now();

        keepFocus(this.root, () => this.#standings.replaceChildren(...view.teams.map((team) => {
            const color = getTeamColor(this.#teamColors, team.id);
            const open = h(
                "button",
                { "type": "button", "class": "team", "data-focus-key": `standing-${team.id}` },
                `${TEAM_COLOR_LABELS[color]} Team`
            );

            open.addEventListener("click", () => this.#openHistory(team.id));

            const row = h(
                "tr",
                {},
                h("th", { scope: "row" }, open),
                h("td", {}, String(team.score))
            );

            // The line under the total is always laid out, and only shown while
            // a round is counted, so the count does not push the feed down.
            const counted = h(
                "tr",
                { "class": "round", "aria-hidden": counting === null ? "true" : null },
                h("th", { scope: "row" }, "This round"),
                h("td", {}, formatPoints(counting?.progress.points.get(team.id) ?? 0))
            );

            const group = h("tbody", {}, row, counted);

            group.style.setProperty("--team", `var(--team-${color})`);

            // Rebuilt with every card counted, so the flash picks up where it was.
            if (counting?.penalty?.teamId === team.id) {
                group.classList.add("penalized");
                group.style.setProperty("--penalty-delay", `${Math.round(counting.penalty.start - now)}ms`);
            }

            return group;
        })));
    }

    #openHistory(teamId: string): void {
        this.#historyTeamId = teamId;
        this.render();

        if (!this.#historyDialog.open) {
            this.#historyDialog.showModal();
        }
    }

    #closeHistory(): void {
        this.#historyTeamId = null;

        if (this.#historyDialog.open) {
            this.#historyDialog.close();
        }
    }

    /** Redrawn with every update, so the estimate keeps pace with the table. */
    #renderHistory(view: GameView): void {
        const teamId = this.#historyTeamId;

        if (teamId === null) {
            this.#historyView.model = null;
            return;
        }

        const color = getTeamColor(this.#teamColors, teamId);

        this.#historyDialog.setAttribute("aria-label", `${TEAM_COLOR_LABELS[color]} Team, scores by round`);
        this.#historyView.model = { view, teamId, color };
    }

    #renderTray(view: GameView, options: TurnOptions): void {
        const tray = this.#tray;

        if (tray === null) {
            this.#trayView.model = null;
            return;
        }

        const name = getTeamName(view, tray.teamId);
        const isOwn = tray.teamId === options.team?.id;

        this.#trayDialog.setAttribute("aria-label", isOwn ? `${name}, ${tray.kind}` : `${name}, ${tray.kind}, read-only`);
        this.#trayView.model = {
            team: this.#teamFor(view, tray.teamId),
            kind: tray.kind,
            name,
            color: getTeamColor(this.#teamColors, tray.teamId),
            isOwn,
            addableRanks: options.addableRanks
        };
    }

    /** The summary of a finished round, which waits for the round to be counted out. */
    #renderInterlude(view: GameView): void {
        const showing = view.status !== "in-round" && !this.#isCounting();
        const appearing = showing && this.#interlude.hidden;
        const complete = view.status === "game-complete";

        this.#interlude.hidden = !showing;

        if (!showing) {
            return;
        }

        this.#interludeHeading.textContent = complete ? "Game over" : `Round ${view.round} scored`;
        this.#interludeNote.textContent = complete
            ? "Leave the table to head back to the saloon."
            : "The next round deals in a moment.";
        this.#backButton.hidden = !complete;
        this.#scores.model = { view, teamColors: this.#teamColors };

        // The summary takes focus, so keyboard players are not left behind it.
        if (appearing) {
            this.#interludeHeading.focus();
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "game-table": GameTable;
    }
}

customElements.define("game-table", GameTable);
